import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export const usePasswordManager = () => {
  const [passwords, setPasswords] = useState([]);
  const [allUniqueCommunities, setAllUniqueCommunities] = useState([]); // Store all communities regardless of filter
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentFilterCommunity, setCurrentFilterCommunity] = useState('all'); // Track the active filter
  const [totalCount, setTotalCount] = useState(0); // Store total count separately
  const [activeCount, setActiveCount] = useState(0); // Store active count separately
  const [totalUsages, setTotalUsages] = useState(0); // Store total usages separately
  const { toast } = useToast();

  // Fetches user authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data: { user }
        } = await supabase.auth.getUser();
        setUser(user);
      } catch (err) {
        console.error('Auth check error:', err);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch all unique communities for dropdown - independent of filter
  const fetchAllUniqueCommunities = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('community_passwords')
        .select('community_name')
        .not('community_name', 'is', null);

      if (fetchError) throw fetchError;

      const uniqueCommunitiesSet = new Set();
      data.forEach((p) => p.community_name && uniqueCommunitiesSet.add(p.community_name));
      const communities = Array.from(uniqueCommunitiesSet).sort();
      
      setAllUniqueCommunities(communities);
    } catch (err) {
      console.error('Error fetching unique communities:', err);
      // Don't show error toast for this background operation
    }
  }, []);

  // Fetch counts only - no actual data for better performance
  const fetchPasswordCounts = useCallback(async () => {
    try {
      // Get total count
      const { count: totalPasswordCount, error: totalError } = await supabase
        .from('community_passwords')
        .select('*', { count: 'exact', head: true });

      if (totalError) throw totalError;

      // Get active count
      const { count: activePasswordCount, error: activeError } = await supabase
        .from('community_passwords')
        .select('*', { count: 'exact', head: true })
        .eq('active', true);

      if (activeError) throw activeError;

      // Get total usages (sum of all current_uses)
      const { data: usageData, error: usageError } = await supabase
        .from('community_passwords')
        .select('current_uses');

      if (usageError) throw usageError;

      // Sum up all current_uses
      const totalUsageCount = usageData?.reduce((sum, item) => sum + (item.current_uses || 0), 0) || 0;

      setTotalCount(totalPasswordCount || 0);
      setActiveCount(activePasswordCount || 0);
      setTotalUsages(totalUsageCount);
    } catch (err) {
      console.error('Error fetching password counts:', err);
      // Don't show error toast for this background operation
    }
  }, []);

  // MAIN DATA FETCHING FUNCTION - NOW SUPABASE-FILTERED
  const fetchPasswords = useCallback(
    async (filter = 'all') => {
      setLoading(true);
      setError(null);

      try {
        const pageSize = 100;
        const page = 0; // 0 for first page, 1 for second, etc.

        let query = supabase
          .from('community_passwords')
          .select('*')
          .order('created_at', { ascending: false })
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (filter !== 'all') {
          query = query.eq('community_name', filter); // APPLY FILTER AT THE DATABASE LEVEL
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        setPasswords(data || []);
        setCurrentFilterCommunity(filter); // Update internal filter state after successful fetch
      } catch (err) {
        console.error('Error fetching passwords:', err);
        setError('Failed to load passwords');
        toast({
          title: 'Fetch Failed',
          description: 'Failed to load passwords. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  // Realtime Subscription with debouncing for better performance
  useEffect(() => {
    // Parallel initial fetch for better performance
    Promise.all([
      fetchPasswords(currentFilterCommunity),
      fetchAllUniqueCommunities(),
      fetchPasswordCounts()
    ]).catch(err => {
      console.error('Error during initial data fetch:', err);
    });

    // Debounce realtime updates to prevent excessive re-fetching
    let updateTimeout;
    
    const passwordChannel = supabase
      .channel('public:community_passwords')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'community_passwords' }, (payload) => {
        // Clear any pending updates
        clearTimeout(updateTimeout);
        
        // Debounce updates by 500ms to prevent rapid-fire updates
        updateTimeout = setTimeout(() => {
          const affectedPassword = payload.new || payload.old;
          if (currentFilterCommunity === 'all' || affectedPassword.community_name === currentFilterCommunity) {
            fetchPasswords(currentFilterCommunity);
          }
          // Refresh counts on any change that could affect totals
          fetchPasswordCounts();
          // Only refresh community list on INSERT/DELETE events that could add new communities
          if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
            fetchAllUniqueCommunities();
          }
        }, 500);
      })
      .subscribe();

    return () => {
      clearTimeout(updateTimeout);
      supabase.removeChannel(passwordChannel);
    };
  }, [fetchPasswords, fetchAllUniqueCommunities, currentFilterCommunity]);

  // Verification function (already good, but now ensure it uses the direct supabase client)
  const verifyPasswordInDB = useCallback(async (passwordToVerify) => {
    try {
      // Use select().single() for efficient lookup if you expect 0 or 1 result
      const { data, error } = await supabase
        .from('community_passwords')
        .select('id, password, community_name, active, current_uses, max_uses')
        .eq('password', passwordToVerify)
        .maybeSingle(); // Use maybeSingle to avoid error if no row found

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "no rows found" error code
        console.error('Error verifying password in DB:', error.message);
        return null;
      }
      return data; // Will be null if not found, or the object if found
    } catch (e) {
      console.error('Unexpected error during password verification:', e);
      return null;
    }
  }, []);

  // Memoized derived data (these will now operate on the ALREADY FILTERED `passwords` array)
  const derivedData = useMemo(() => {
    // Use allUniqueCommunities for dropdown (includes all communities regardless of filter)
    // But calculate stats only from currently filtered passwords
    const communityStats = {};
    passwords.forEach((p) => {
      if (p.community_name) {
        if (!communityStats[p.community_name]) {
          communityStats[p.community_name] = {
            total: 0,
            active: 0,
            used: 0,
            available: 0
          };
        }
        const stats = communityStats[p.community_name];
        stats.total++;
        if (p.active) stats.active++;

        const currentUses = p.current_uses || 0;
        const maxUses = p.max_uses || 1;

        if (p.active && currentUses < maxUses) {
          stats.available++;
        } else {
          stats.used++; // Inactive or active but exhausted
        }
      }
    });

    return { communityStats };
  }, [passwords]);

  const createPassword = useCallback(
    async (passwordData) => {
      if (!user) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to create passwords.',
          variant: 'destructive'
        });
        return { success: false, error: new Error('Authentication required') };
      }
      try {
        const { data: insertedData, error: insertError } = await supabase
          .from('community_passwords')
          .insert([passwordData])
          .select('*');

        if (insertError) throw insertError;

        toast({
          title: 'Password Created',
          description: `Password "${insertedData[0].password}" created successfully!`,
          variant: 'default'
        });
        return { success: true, data: insertedData[0] };
      } catch (err) {
        console.error('Error creating password:', err);
        toast({
          title: 'Creation Failed',
          description: err.message || 'Failed to create password. Please try again.',
          variant: 'destructive'
        });
        return { success: false, error: err };
      }
    },
    [toast, user]
  );

  const createBulkPasswords = useCallback(
    async (passwordsData) => {
      if (!user) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to create passwords.',
          variant: 'destructive'
        });
        return { success: false, error: new Error('Authentication required') };
      }
      try {
        const { data: insertedData, error: insertError } = await supabase
          .from('community_passwords')
          .insert(passwordsData)
          .select('*');

        if (insertError) throw insertError;

        toast({
          title: 'Bulk Passwords Created',
          description: `Successfully created ${insertedData.length} passwords!`,
          variant: 'default'
        });
        return { success: true, data: insertedData };
      } catch (err) {
        console.error('Error creating bulk passwords:', err);
        toast({
          title: 'Bulk Creation Failed',
          description: err.message || 'Failed to create bulk passwords. Please try again.',
          variant: 'destructive'
        });
        return { success: false, error: err };
      }
    },
    [toast, user]
  );

  const updatePasswordStatus = useCallback(
    async (id, newStatus) => {
      if (!user) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to update passwords.',
          variant: 'destructive'
        });
        return { success: false, error: new Error('Authentication required') };
      }
      try {
        const { error: updateError } = await supabase
          .from('community_passwords')
          .update({ active: newStatus })
          .eq('id', id);

        if (updateError) throw updateError;

        toast({
          title: 'Status Updated',
          description: `Password has been ${newStatus ? 'activated' : 'deactivated'} successfully!`,
          variant: 'default'
        });
        return { success: true };
      } catch (err) {
        console.error('Error updating password:', err);
        toast({
          title: 'Update Failed',
          description: err.message || 'Failed to update password status. Please try again.',
          variant: 'destructive'
        });
        return { success: false, error: err };
      }
    },
    [toast, user]
  );

  const deletePassword = useCallback(
    async (id) => {
      if (!user) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to delete passwords.',
          variant: 'destructive'
        });
        return { success: false, error: new Error('Authentication required') };
      }
      try {
        const { data: existingPassword, error: fetchError } = await supabase
          .from('community_passwords')
          .select('password')
          .eq('id', id)
          .maybeSingle();

        if (fetchError || !existingPassword) {
          throw new Error('Password not found or inaccessible');
        }

        const { error: deleteError } = await supabase.from('community_passwords').delete().eq('id', id);

        if (deleteError) throw deleteError;

        toast({
          title: 'Password Deleted',
          description: `Password "${existingPassword.password}" has been deleted successfully!`,
          variant: 'default'
        });
        return { success: true };
      } catch (err) {
        console.error('Error deleting password:', err);
        toast({
          title: 'Delete Failed',
          description: err.message || 'Failed to delete password. Please try again.',
          variant: 'destructive'
        });
        return { success: false, error: err };
      }
    },
    [toast, user]
  );

  const getFilteredPasswords = useCallback(() => {
    // This function is no longer needed as filtering happens in fetchPasswords
    // It's kept for API compatibility, but will just return the already filtered list
    return passwords;
  }, [passwords]);

  const resetPasswordForReuse = useCallback(
    async (passwordId) => {
      if (!user) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to reset passwords.',
          variant: 'destructive'
        });
        return { success: false, error: new Error('Authentication required') };
      }
      try {
        const { data: existingPassword, error: fetchError } = await supabase
          .from('community_passwords')
          .select('password')
          .eq('id', passwordId)
          .maybeSingle();

        if (fetchError || !existingPassword) {
          throw new Error('Password not found or inaccessible');
        }

        const { error: updateError } = await supabase
          .from('community_passwords')
          .update({ 
            current_uses: 0,
            active: true
          })
          .eq('id', passwordId);

        if (updateError) throw updateError;

        // TODO: Clear user associations in your user registration table
        // You'll need to add this when you implement user-password tracking
        // Example: await clearUserAssociations(passwordId);

        toast({
          title: 'Password Reset',
          description: `Password "${existingPassword.password}" has been reset and is now available for reuse!`,
          variant: 'default'
        });
        return { success: true };
      } catch (err) {
        console.error('Error resetting password:', err);
        toast({
          title: 'Reset Failed',
          description: err.message || 'Failed to reset password. Please try again.',
          variant: 'destructive'
        });
        return { success: false, error: err };
      }
    },
    [toast, user]
  );

  const deleteCommunity = useCallback(
    async (communityName) => {
      if (!user) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to delete communities.',
          variant: 'destructive'
        });
        return { success: false, error: new Error('Authentication required') };
      }

      try {
        // First, get count of passwords that will be deleted
        const { data: passwordsToDelete, error: countError } = await supabase
          .from('community_passwords')
          .select('id, password')
          .eq('community_name', communityName);

        if (countError) throw countError;

        if (!passwordsToDelete || passwordsToDelete.length === 0) {
          toast({
            title: 'Community Not Found',
            description: `No passwords found for community "${communityName}".`,
            variant: 'destructive'
          });
          return { success: false, error: new Error('Community not found') };
        }

        // Delete all passwords for this community
        const { error: deleteError } = await supabase
          .from('community_passwords')
          .delete()
          .eq('community_name', communityName);

        if (deleteError) throw deleteError;

        // TODO: Also delete related user registrations if needed
        // const { error: registrationError } = await supabase
        //   .from('whitelist_registrations')
        //   .delete()
        //   .eq('community_password_used', communityName);
        
        toast({
          title: 'Community Deleted Successfully',
          description: `Deleted community "${communityName}" and ${passwordsToDelete.length} related password${passwordsToDelete.length !== 1 ? 's' : ''}.`,
          variant: 'default'
        });

        return { 
          success: true, 
          deletedCount: passwordsToDelete.length,
          communityName 
        };
      } catch (err) {
        console.error('Error deleting community:', err);
        toast({
          title: 'Community Deletion Failed',
          description: err.message || 'Failed to delete community. Please try again.',
          variant: 'destructive'
        });
        return { success: false, error: err };
      }
    },
    [toast, user]
  );

  const getGroupedPasswords = useCallback((passwordsToGroup) => {
    const grouped = {};
    passwordsToGroup.forEach((password) => {
      const communityName = password.community_name || 'Unnamed';
      if (!grouped[communityName]) {
        grouped[communityName] = [];
      }
      grouped[communityName].push(password);
    });
    return grouped;
  }, []);

  return {
    passwords,
    loading,
    error,
    user,
    authLoading,
    uniqueCommunities: allUniqueCommunities, // Now uses all communities regardless of filter
    communityStats: derivedData.communityStats,
    totalCount, // Total count of all passwords
    activeCount, // Count of active passwords only
    totalUsages, // Total usage count from all passwords
    fetchPasswords, // Exposed to allow component to trigger filtered fetches
    createPassword,
    createBulkPasswords,
    updatePasswordStatus,
    deletePassword,
    resetPasswordForReuse,
    deleteCommunity,
    getFilteredPasswords, // For backward compatibility; returns already filtered data
    getGroupedPasswords,
    verifyPasswordInDB
  };
};
